import { appNameFromPath } from "../runtime/stage-router.js";
import { escapeHtml } from "../util/escape-html.js";
import { resolveStageCatalogEntry, stageIconMarkup, STAGE_GROUPS } from "./stage-catalog.js";
const SEARCH_ICON_PATH = '<circle cx="8.5" cy="8.5" r="5.5"/><path d="M12.7 12.7 L17.5 17.5"/>';
/**
 * Port of the original skin's `#pmMore` "Stages + more" mega-panel (`.node-design/
 * original-skin/index.html` ~lines 100-113, wired by `core/shell/menu/index.js`'s
 * `renderMoreMenuFromRegistry()`/`setFly`/`positionFlyouts` and filtered by
 * `core/shell/menu/search.js`) — epic #1507 fidelity F5.
 *
 * The source builds `.pmore-cols` from one `.pmore-sec` PER COLORED REGISTRY GROUP
 * (`renderMoreMenuFromRegistry()` in `core/shell/menu/index.js` — 5 groups: Default/Agents/
 * Stages/Network/System, each with its own `--sec` color read by the vendored `menu.css`'s
 * colored 24x24 `.pmenu-ic` tile + section-header + hover-tint rules). This app's stage
 * catalog is dynamic (`stages.installed.v1`) rather than the source's fixed product list, so
 * grouping is done by `stage-catalog.ts`'s `resolveStageCatalogEntry` (keyed off the
 * installed app id extracted from each item's path via `appNameFromPath`) — a known id gets
 * the original's own group/icon; an unrecognized one falls back to the STAGES group with a
 * generic tile rather than being dropped. Only NON-EMPTY groups render a section, in the
 * source's own fixed group order (`STAGE_GROUPS`).
 *
 * `.pmore-recents` mirrors the source's own `recentApps`/`pushRecent`/`renderRecents`
 * structural slot (`core/shell/menu/index.js` ~721-767): a session-local (in-memory, resets
 * on reload — matching the source's own in-memory array) list of up to 3 most-recently-opened
 * stage paths, rendered as `.pm-recent` chips. The push/cap-3/dedupe logic lives in
 * `pmenu.ts` (the owning custom element, which is where the click that "opens a stage" is
 * actually observed); this module only renders whatever `recentPaths` it's given, skipping
 * any path no longer present in `items` (an uninstalled stage) exactly like the source's own
 * `recentMeta()` returning `null` for a missing entry.
 */
export function renderPmoreMarkup(options) {
    const { currentPath, items, open, recentPaths } = options;
    return `
    <div
      id="pmMore"
      class="pmore${open ? " open" : ""}"
      aria-hidden="${open ? "false" : "true"}"
      ${open ? "" : "inert"}
      data-pmore
    >
      <div class="pmore-hd">
        <div class="pmore-hd-tx">
          <div class="pmore-title">Recent</div>
          <div class="pmore-recents" data-pmore-recents aria-label="Recent stages">${renderRecents(recentPaths, items)}</div>
        </div>
        <div class="pmore-search-wrap">
          <svg class="pmore-search-ic" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round">${SEARCH_ICON_PATH}</svg>
          <input
            class="pmore-search"
            data-pmore-search
            type="text"
            placeholder="Search stages&hellip;"
            spellcheck="false"
            autocomplete="off"
          >
        </div>
      </div>
      <div class="pmore-cols" data-pmore-cols aria-live="polite">
        ${renderGroups(items, currentPath)}
      </div>
    </div>
  `;
}
/**
 * Submenu search — port of the source's `core/shell/menu/search.js`: filter entries by
 * label substring, case-insensitive, toggling the same `.pm-hidden` class the vendored
 * `fixes.css` already styles (`.pmore-sec.pm-hidden, .pmore .pmenu-item.pm-hidden
 * { display: none !important; }`). Also hides a whole `.pmore-sec` once every entry inside
 * it is hidden — matching the source's own `sec.classList.toggle('pm-hidden', !!q && !any)`
 * — so an empty colored section doesn't leave a dangling header + divider behind.
 */
export function filterPmoreEntries(root, query) {
    const needle = query.trim().toLowerCase();
    const sections = root.querySelectorAll("[data-pmore-sec]");
    for (const section of sections) {
        let anyVisible = false;
        const entries = section.querySelectorAll("[data-pmore-entry]");
        for (const entry of entries) {
            const label = entry.querySelector(".pmenu-lb")?.textContent?.toLowerCase() ?? "";
            const hit = needle === "" || label.includes(needle);
            entry.classList.toggle("pm-hidden", !hit);
            if (hit) {
                anyVisible = true;
            }
        }
        section.classList.toggle("pm-hidden", needle !== "" && !anyVisible);
    }
}
function renderGroups(items, currentPath) {
    return STAGE_GROUPS.map((group) => {
        const groupItems = items.filter((item) => resolveStageCatalogEntry(appNameFromPath(item.path)).group === group.id);
        if (groupItems.length === 0) {
            return "";
        }
        return `
      <div class="pmore-sec" style="--sec: ${group.color};" data-pmore-sec>
        <div class="pmore-sec-hd"><span class="pmore-chip"></span><span>${escapeHtml(group.label)}</span></div>
        ${groupItems.map((item) => renderPmoreEntry(item, currentPath)).join("")}
      </div>
    `;
    }).join("");
}
function renderRecents(recentPaths, items) {
    return recentPaths
        .map((path) => items.find((item) => item.path === path))
        .filter((item) => item !== undefined)
        .map((item) => {
        const icon = resolveStageCatalogEntry(appNameFromPath(item.path)).icon;
        return `
        <button class="pm-recent" data-path="${item.path}" type="button">
          <span class="pmenu-ic"><svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${stageIconMarkup(icon)}</svg></span>
          <b>${escapeHtml(item.label)}</b>
        </button>
      `;
    })
        .join("");
}
function renderPmoreEntry(item, currentPath) {
    const current = item.path === currentPath;
    const icon = resolveStageCatalogEntry(appNameFromPath(item.path)).icon;
    return `
    <button
      class="pmenu-item${current ? " active" : ""}"
      data-path="${item.path}"
      data-pmore-entry
      aria-current="${current ? "page" : "false"}"
      title="${escapeHtml(item.label)}"
      type="button"
    >
      <span class="pmenu-ic"><svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">${stageIconMarkup(icon)}</svg></span>
      <span class="pmenu-lb">${escapeHtml(item.label)}</span>
    </button>
  `;
}
//# sourceMappingURL=pmore.js.map